
import { Navigation } from "@/components/Navigation";
import headerBgMobile from "@/assets/header-bg-mobile.jpg";
import { Feed } from "@/components/Feed";
import { OptimizedFeedContainer } from "@/components/feed/OptimizedFeedContainer";
import { Profile } from "@/components/Profile";
import { Discover } from "@/components/Discover";
import { Trends } from "@/components/Trends";
import { Prices } from "@/components/Prices";
import { Admin } from "@/components/Admin";
import { PostCreator } from "@/components/PostCreator";
import { TagTeams } from "@/components/TagTeams";
import { CommunityGuidelines } from "@/components/CommunityGuidelines";
import { useAppTimeTracking } from "@/hooks/useAppTimeTracking";
import { isTabEnabled, getCurrentVersionFeatures } from "@/config/featureFlags";
import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLocation } from "react-router-dom";

interface MainAppProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  isLeftRailCollapsed?: boolean;
}

export const MainApp = ({ activeTab, onTabChange, isLeftRailCollapsed = false }: MainAppProps) => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [shouldEditProfile, setShouldEditProfile] = useState(false);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Check if user should be directed to profile edit
  useEffect(() => {
    if (location.pathname === "/profile/edit" && user) {
      setShouldEditProfile(true);
      onTabChange("profile");
      // Clear the URL to remove the /profile/edit part
      window.history.replaceState({}, '', '/');
    }
  }, [location.pathname, user, onTabChange]);

  // Track total time spent on the app
  useAppTimeTracking();

  // Background image preloading - runs once
  useEffect(() => {
    const img = new Image();
    img.src = headerBgMobile;
  }, []);

  const renderActiveComponent = useMemo(() => {
    // Check if the current tab is enabled
    if (!isTabEnabled(activeTab)) {
      onTabChange("home");
      return <Feed />;
    }

    switch (activeTab) {
      case "home":
        return <Feed onTabChange={onTabChange} />;
      case "discover":
        return <Discover />;
      case "trends":
        return <Trends />;
      case "tag-teams":
        return <TagTeams />;
      case "admin":
        return isTabEnabled("admin") ? <Admin /> : <Feed />;
      case "usda-integration":
        return isTabEnabled("usda-integration") ? <Prices /> : <Feed />;
      case "create":
        return <PostCreator onBack={() => onTabChange("home")} />;
      case "guidelines":
        return <CommunityGuidelines />;
      case "profile":
        return <Profile initialEditMode={shouldEditProfile} onEditModeUsed={() => setShouldEditProfile(false)} />;
      default:
        return <Feed />;
    }
  }, [activeTab, onTabChange, user]);

  return (
    <div className="min-h-screen relative">
      {/* Fixed Background Image */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat z-0"
        style={{
          backgroundImage: `url(${headerBgMobile})`
        }}
      />
      
      {/* Content column - document scroll enabled */}
      <div className="relative z-10 w-full mx-auto max-w-2xl bg-white/5 backdrop-blur-sm shadow-xl pb-20">
        {renderActiveComponent}
      </div>

      {/* Bottom Navigation - Fixed to viewport, offset for left rail */}
      <div 
        className={`fixed bottom-0 left-0 right-0 w-full z-50 transition-[left] duration-200 ${
          isLeftRailCollapsed ? 'md:left-[80px]' : 'md:left-[240px]'
        } md:w-auto`}
      >
        <div className="w-full max-w-2xl mx-auto">
          <Navigation activeTab={activeTab} onTabChange={onTabChange} />
        </div>
      </div>
    </div>
  );
};
